import PageMeta from "../../../../shared/ui/common/PageMeta";
import { useTherapist } from "../../../../context/useTherapist";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { useHomeData, type PatientAgreementStats } from "../../application/useHomeData";
import { Loader2, Zap, Activity, FileText, CheckSquare, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { useSubscription } from "../../../subscription";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";
import { useMotionSafe } from "../../../../hooks/useMotionSafe";
import {
  EmptyState,
  PageHeader,
  ProgressBar,
  SectionLabel,
  StatCard,
  Surface,
} from "../../../../shared/ui/primitives";
import HomeToolDock from "../components/HomeToolDock";
import HomeNextSession from "../components/HomeNextSession";
import HomePendingPayments from "../components/HomePendingPayments";

export default function Home() {
  const { t, i18n } = useTranslation();
  const { therapist } = useTherapist();
  const { isPremium } = useSubscription();
  const { isMobile, isTablet, breakpoint } = useBreakpoint();
  const { container, item, transition } = useMotionSafe();
  const isSmallScreen = isMobile || isTablet || breakpoint === "lg";

  const { recentSessions, loadingSessions, homeStats } = useHomeData(therapist?.id);

  const agreements = homeStats?.agreements;
  const agreementsCompletedPct =
    agreements && agreements.total > 0
      ? Math.round((agreements.completed / agreements.total) * 100)
      : 0;
  const agreementsByPatient = homeStats?.agreements_by_patient ?? [];

  const sessionCount = therapist?.monthly_session_count ?? 0;
  const sessionLimit = therapist?.monthly_session_limit ?? null;
  const usagePct = sessionLimit
    ? Math.round(Math.min((sessionCount / sessionLimit) * 100, 100))
    : null;

  const today = new Date();
  const loadingLabel = (label: string) => t("common.loadingValue", { label });

  return (
    <>
      <PageMeta title={t("home.pageTitle")} description={t("home.description")} />

      <motion.div
        variants={container}
        initial="hidden"
        animate="visible"
        className="mx-auto w-full max-w-[1400px] space-y-8 pb-16 sm:space-y-10"
      >
        <motion.div variants={item} transition={transition}>
          <PageHeader
            eyebrow={today.toLocaleDateString(i18n.language, {
              weekday: "long",
              day: "numeric",
              month: "long",
            })}
            title={
              <>
                {t("home.welcomePart1")}{" "}
                {/* The one brand moment on this screen: the spectrum on the
                    therapist's own name, at a size where a gradient is legible. */}
                <span className="zen-text-gradient">
                  {therapist?.first_name || t("home.welcomeFallback")}
                </span>
              </>
            }
            description={t("home.welcomeSubtitle")}
          />
        </motion.div>

        {/* Each of these renders nothing unless its tool is adopted, so `auto-fit`
            is doing real work: with one absent the other fills the row rather
            than sitting half-width beside a gap. */}
        <motion.div
          variants={item}
          transition={transition}
          className="grid gap-4 sm:gap-6 [grid-template-columns:repeat(auto-fit,minmax(20rem,1fr))]"
        >
          <HomeNextSession />
          <HomePendingPayments />
        </motion.div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-6">
          <motion.div variants={item} transition={transition}>
            <StatCard
              label={t("home.insights.pendingSessions")}
              value={homeStats?.pending_sessions}
              loadingLabel={loadingLabel(t("home.insights.pendingSessions"))}
              tone="accent"
              icon={<Zap className="size-5" />}
            />
          </motion.div>

          <motion.div variants={item} transition={transition}>
            <StatCard
              label={t("home.insights.pendingNotes")}
              value={homeStats?.pending_notes}
              loadingLabel={loadingLabel(t("home.insights.pendingNotes"))}
              tone="warning"
              icon={<FileText className="size-5" />}
            />
          </motion.div>

          <motion.div variants={item} transition={transition}>
            <StatCard
              label={t("home.insights.agreements")}
              value={agreements ? `${agreements.completed}/${agreements.total}` : undefined}
              loadingLabel={loadingLabel(t("home.insights.agreements"))}
              tone="success"
              icon={<CheckSquare className="size-5" />}
              badge={
                agreements && agreements.total > 0 ? (
                  <span className="rounded-full bg-status-success-tint px-2.5 py-1 text-xs font-semibold tabular-nums text-status-success-on-tint">
                    {agreementsCompletedPct}%
                  </span>
                ) : undefined
              }
            >
              {agreementsByPatient.length > 0 && (
                <ul className="custom-scrollbar max-h-40 space-y-3 overflow-y-auto pr-1">
                  {agreementsByPatient.map((patient: PatientAgreementStats) => (
                    <li key={patient.patient_id} className="space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-medium text-content-primary">
                          {patient.patient_name}
                        </span>
                        <span className="shrink-0 text-xs font-semibold tabular-nums text-content-secondary">
                          {patient.completed}/{patient.total}
                        </span>
                      </div>
                      <ProgressBar
                        size="sm"
                        label={`${patient.patient_name} — ${t("home.insights.agreementsProgress")}`}
                        valueText={`${patient.completed}/${patient.total}`}
                        max={patient.total}
                        segments={[
                          { value: patient.completed, tone: "success" },
                          { value: patient.in_progress, tone: "warning" },
                        ]}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </StatCard>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:gap-6 xl:grid-cols-12">
          <motion.div variants={item} transition={transition} className="xl:col-span-7">
            <StatCard
              label={t("home.insights.monthlyUsage")}
              value={sessionCount}
              unit={`/ ${sessionLimit ?? "∞"}`}
              tone="accent"
              icon={<Activity className="size-5" />}
              badge={
                usagePct !== null ? (
                  <span className="rounded-full bg-accent-tint px-2.5 py-1 text-xs font-semibold tabular-nums text-accent-on-tint">
                    {usagePct}%
                  </span>
                ) : undefined
              }
            >
              <ProgressBar
                label={t("home.insights.monthlyUsage")}
                valueText={`${sessionCount} / ${sessionLimit ?? "∞"}`}
                max={sessionLimit ?? (sessionCount || 1)}
                segments={[{ value: sessionLimit ? sessionCount : 1, tone: "accent" }]}
              />
            </StatCard>
          </motion.div>

          <motion.div variants={item} transition={transition} className="xl:col-span-5">
            <Surface as="section" aria-labelledby="recent-sessions" className="h-full">
              <div className="mb-5 flex items-center justify-between gap-3">
                <SectionLabel as="h2" id="recent-sessions">
                  {t("home.recentSessionsTitle")}
                </SectionLabel>
                {/* No "view all": sessions are read in each patient's record,
                    and every row below opens its own. */}
              </div>

              {loadingSessions ? (
                <div className="flex justify-center py-10">
                  <Loader2
                    aria-label={loadingLabel(t("home.recentSessionsTitle"))}
                    role="status"
                    className="size-6 animate-spin text-accent-text"
                  />
                </div>
              ) : recentSessions.length > 0 ? (
                <ul className="space-y-2">
                  {recentSessions.map((session) => (
                    <li key={session.id}>
                      <Link
                        to={`/session/${session.id}`}
                        className="flex items-center gap-3 rounded-lg border border-transparent p-3 transition-colors duration-[--duration-fast] hover:border-border-subtle hover:bg-surface-sunken/60"
                      >
                        <span
                          aria-hidden="true"
                          className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-accent-tint text-sm font-semibold text-accent-on-tint"
                        >
                          {session.patient_name?.[0]}
                          {session.patient_last_name?.[0]}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-content-primary">
                            {session.patient_name} {session.patient_last_name}
                          </span>
                          <span className="block truncate text-sm text-content-secondary">
                            {session.title || t("session.untitledSession")}
                          </span>
                        </span>
                        <span className="shrink-0 text-xs font-medium tabular-nums text-content-secondary">
                          {new Date(session.date).toLocaleDateString(i18n.language, {
                            day: "2-digit",
                            month: "short",
                          })}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState
                  icon={<FileText className="size-5" />}
                  title={t("home.noRecentSessions")}
                />
              )}
            </Surface>
          </motion.div>
        </div>

        {!isPremium && isSmallScreen && (
          <motion.div variants={item} transition={transition}>
            <Surface tone="accent" padding="lg" radius="2xl">
              <SectionLabel className="mb-3 text-accent-on-tint">
                {t("sidebar.plusPlan")}
              </SectionLabel>
              <h2 className="mb-3 text-xl font-semibold tracking-tight text-content-primary">
                {t("sidebar.plusPlanDesc")}
              </h2>
              <p className="mb-6 max-w-lg text-sm leading-relaxed text-content-secondary">
                {t("subscription.plus.description")}
              </p>
              <Link
                to="/subscription"
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-accent px-4 py-2.5 text-sm font-semibold text-accent-contrast transition-colors duration-[--duration-fast] hover:bg-accent-hover"
              >
                {t("subscription.plus.button")}
                <ArrowRight aria-hidden="true" className="size-4" />
              </Link>
            </Surface>
          </motion.div>
        )}
      </motion.div>

      <HomeToolDock />
    </>
  );
}
